import {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
  createContext,
  useContext,
} from 'react';
import { BehaviorSubject, Subject, combineLatest, from, defer, Observable, merge, of } from 'rxjs';
import {
  map,
  tap,
  distinctUntilChanged,
  finalize,
  throttle,
  throttleTime,
  exhaustMap,
  take,
  refCount,
  publishReplay,
  debounceTime,
  skip,
  catchError,
} from 'rxjs/operators';
import { useStore, useDispatch } from 'react-redux';
import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import { useSetLoading } from './components/LoadingLayer';
import appConfig from './config';
import {
  GRANULARITY_OPTIONS,
  EVENT_TYPE_DTMP,
  EVENT_TYPE_USER,
  EVENT_TYPE_MACH,
  DTMP_SEVERITIES,
} from './constants';
import { getLocalDateString } from './datetimeUtils';
import {
  addSubscriptions as addVariableSubscriptions,
  removeSubscriptions as removeVariableSubscriptions,
} from './bundles/variables';

let uniqueId = 1;
const getUniqueId = () => uniqueId++;

const getRealm = () => {
  const { auth: authConfig = {}, domain = '' } = appConfig;
  let { realm } = authConfig;
  const domainIndex = domain ? globalThis.location.hostname.indexOf(domain) : -1;
  if (domainIndex >= 0) {
    realm = globalThis.location.hostname.slice(0, domainIndex).split('.')[0] || 'master';
  }
  return realm || 'master';
};

const checkNetworkStatus = async () => {
  if (appConfig?.onPremise) {
    return true;
  }
  if (!navigator.onLine) {
    return false;
  }
  return fetch('https://google.com', { mode: 'no-cors' })
    .then(() => true)
    .catch(() => false);
};

const useComponentId = () => {
  const idReference = useRef(getUniqueId());
  return idReference.current;
};

const truncateFilename = (s, n = 20, tail = 8) => {
  if (s.length <= n + 2 || s.length <= tail) {
    return [s, false];
  }
  return [`${s.slice(0, Math.max(0, n - tail))}…${s.slice(-1 * tail)}`, true];
};

const isFunction = (function_) =>
  !!(function_ && function_.constructor && function_.call && function_.apply);

const updateSettingsParameters = (search, update) => {
  const params = new URLSearchParams(search);
  Object.entries(update).forEach(([k, v]) => {
    if (v === null) {
      params.delete(k);
    } else {
      params.set(k, v);
    }
  });
  return `?${params.toString()}`;
};

const upsert = (array, item) => {
  const index = array.findIndex((_item) => _item.id === item.id);

  if (index === -1) {
    array.push(item);
  } else {
    array[index] = item;
  }

  return array;
};

const useEventListener = (eventName, handler, element = globalThis) => {
  const savedHandler = useRef();

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const isSupported = element && element.addEventListener;
    if (!isSupported) {
      return () => {
        /* no action */
      };
    }

    const eventListener = (event) => savedHandler.current(event);

    element.addEventListener(eventName, eventListener);

    return () => {
      element.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element]);
};

/**
 * When an onClick handler is added on a parent element the event fires even if
 * you mousedown in a child element and mouseup in the parent element. This is
 * sometimes unwanted behavior. In modals for example, where the modal will
 * close if you mousedown in the modal and mouseup in the overlay.
 *
 * This hook solves that.
 */
const useParentOnlyClickHandler = (handler, parent, parentCanClose = true) => {
  const savedHandler = useRef();
  const mouseDown = useRef(false);

  const mouseDownHandler = (event) => {
    if (event.currentTarget === event.target) {
      mouseDown.current = true;
    }
  };

  const mouseUpHandler = (event) => {
    if (
      typeof savedHandler.current === 'function' &&
      event.currentTarget === event.target &&
      mouseDown.current &&
      parentCanClose
    ) {
      savedHandler.current(event);
    }
    mouseDown.current = false;
  };

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const isSupported = parent?.addEventListener;
    if (!isSupported) {
      return () => {
        /* no action */
      };
    }

    parent.addEventListener('mousedown', mouseDownHandler);
    parent.addEventListener('mouseup', mouseUpHandler);

    return () => {
      parent.removeEventListener('mousedown', mouseDownHandler);
      parent.removeEventListener('mouseup', mouseUpHandler);
    };
  }, [parent, mouseDown]);
};

// Reference-counted so simultaneous/stacked lockers (modals, panels) can't
// prematurely undo each other's freeze - only the first acquire/last release acts.
let backgroundFreezeCount = 0;
let backgroundFreezeOffsetY = 0;

const acquireBackgroundFreeze = () => {
  backgroundFreezeCount += 1;
  if (backgroundFreezeCount > 1) return;

  backgroundFreezeOffsetY = window.pageYOffset;
  const body = document.querySelector('body');
  body.style.top = `${-backgroundFreezeOffsetY}px`;
  body.classList.add('no-scroll');
  if (document.scrollingElement) {
    document.scrollingElement.style['scroll-behavior'] = 'auto';
  }
};

const releaseBackgroundFreeze = () => {
  backgroundFreezeCount = Math.max(0, backgroundFreezeCount - 1);
  if (backgroundFreezeCount > 0) return;

  const body = document.querySelector('body');
  body.classList.remove('no-scroll');
  body.style.removeProperty('top');
  window.scrollTo(0, backgroundFreezeOffsetY);
  if (document.scrollingElement) {
    document.scrollingElement.style['scroll-behavior'] = 'smooth';
  }
};

/**
 * When modals are present nothing behind the modal should be scrollable.
 * Touch devices running Webkit don't honor overflow:hidden in the body tag.
 * So we need to do this. https://bugs.webkit.org/show_bug.cgi?id=153852#c43
 */
const useBackgroundFreeze = () => {
  useEffect(() => {
    acquireBackgroundFreeze();
    return releaseBackgroundFreeze;
  }, []);
};

const useDoubleClick = (
  callback = () => {
    /* no action */
  }
) => {
  const [element, setElement] = useState(null);
  const countReference = useRef(0);
  const timerReference = useRef(null);
  const inputCallbackReference = useRef(null);
  const callbackReference = useCallback((node) => {
    setElement(node);
    callbackReference.current = node;
  }, []);

  useEffect(() => {
    inputCallbackReference.current = callback;
  });

  useEffect(() => {
    function handler(event) {
      const isDoubleClick = countReference.current + 1 === 2;
      const timerIsPresent = timerReference.current;
      if (isDoubleClick) {
        event.stopPropagation();
      }
      if (timerIsPresent && isDoubleClick) {
        clearTimeout(timerReference.current);
        timerReference.current = null;
        countReference.current = 0;
        if (inputCallbackReference.current) {
          inputCallbackReference.current();
        }
      }
      if (!timerIsPresent) {
        countReference.current += 1;
        const timer = setTimeout(() => {
          clearTimeout(timerReference.current);
          timerReference.current = null;
          countReference.current = 0;
        }, 200);
        timerReference.current = timer;
      }
    }
    if (element) {
      element.addEventListener('click', handler);
    }

    return () => {
      if (element) {
        element.removeEventListener('click', handler);
      }
    };
  }, [element]);
  return [callbackReference, element];
};

// Reference-counted for the same reason as backgroundFreeze above.
let scrollBlockCount = 0;

const acquireScrollBlock = () => {
  scrollBlockCount += 1;
  if (scrollBlockCount === 1) {
    document.body.classList.add('scroll-blocked');
  }
};

const releaseScrollBlock = () => {
  scrollBlockCount = Math.max(0, scrollBlockCount - 1);
  if (scrollBlockCount === 0) {
    document.body?.classList.remove('scroll-blocked');
  }
};

const useScrollBlock = () => {
  const scroll = useRef(false);

  const blockScroll = useCallback(() => {
    if (typeof document === 'undefined' || scroll.current) return;

    acquireScrollBlock();
    scroll.current = true;
  }, []);

  const allowScroll = useCallback(() => {
    if (typeof document === 'undefined' || !scroll.current) return;

    releaseScrollBlock();
    scroll.current = false;
  }, []);

  useEffect(() => {
    return () => {
      if (scroll.current) {
        releaseScrollBlock();
        scroll.current = false;
      }
    };
  }, []);

  return [blockScroll, allowScroll];
};

const useActiveSiteId = () => {
  const path = globalThis.location.pathname;
  const sitesIndex = path.indexOf('sites/');
  if (sitesIndex === -1) return null;
  const idAndPath = path.slice(sitesIndex + 6);
  return idAndPath.slice(0, idAndPath.indexOf('/'));
};

const useInterval = (callback, delay) => {
  const savedCallback = useRef();

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};

const useForceUpdate = () => {
  const [, dispatch] = useState(Object.create(null));

  const memoizedDispatch = useCallback(() => {
    dispatch(Object.create(null));
  }, [dispatch]);

  return memoizedDispatch;
};

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = Math.max(decimals, 0);
  const sizes = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const index = Math.floor(Math.log(bytes) / Math.log(k));

  const ki = k ** index;
  return `${Number.parseFloat((bytes / ki).toFixed(dm))}  ${sizes[index]}`;
};

const debounce = (function_, wait) => {
  let timeout;
  return function (...args) {
    const later = () => {
      timeout = null;
      function_(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const useThrottledCallback = (callback, duration, config, dependencies) => {
  const subject = useMemo(() => new Subject(), []);
  useEffect(
    () => {
      const sub = subject.pipe(throttleTime(duration, undefined, config)).subscribe(callback);
      return () => sub.unsubscribe();
    },
    dependencies ? [callback] : dependencies
  );
  return useCallback((arg) => subject.next(arg), [subject]);
};

const useIsMounted = () => {
  const isMountedReference = useRef(true);
  useEffect(
    () => () => {
      isMountedReference.current = false;
    },
    []
  );
  return () => isMountedReference.current;
};

const useDebounce = (callback, delay) => {
  const inputsReference = useRef(callback);
  const isMounted = useIsMounted();
  useEffect(() => {
    inputsReference.current = { cb: callback, delay };
  });

  return useCallback(
    debounce((...args) => {
      // Don't execute callback, if (1) component has been unmounted
      // (2) when inputs meanwhile have changed (old callback)
      if (inputsReference.current.delay === delay && isMounted()) {
        inputsReference.current.cb(...args);
      }
    }, delay),
    [delay, debounce]
  );
};

const sortNumbers = (values, attribute, descending = false) =>
  descending
    ? values.toSorted((a, b) => b[attribute] - a[attribute])
    : values.toSorted((a, b) => a[attribute] - b[attribute]);

const sortStrings = (values, attribute, descending = false) =>
  descending
    ? values.toSorted((a, b) => b[attribute].localeCompare(a[attribute]))
    : values.toSorted((a, b) => a[attribute].localeCompare(b[attribute]));

const sortDates = (values, attribute, descending = false) =>
  descending
    ? values.toSorted((a, b) => new Date(b[attribute]) - new Date(a[attribute]))
    : values.toSorted((a, b) => new Date(a[attribute]) - new Date(b[attribute]));

const sort = (values, attribute, descending = false) => {
  if (values.length === 0) {
    return values;
  }

  const value = values[0][attribute];
  let type = 'string';
  if (Number.isFinite(value)) {
    type = 'number';
  } else if (value instanceof Date) {
    type = 'date';
  }

  let sortedItems;
  switch (type) {
    case 'number': {
      sortedItems = sortNumbers(values, attribute, descending);
      break;
    }
    case 'date': {
      sortedItems = sortDates(values, attribute, descending);
      break;
    }
    default: {
      sortedItems = sortStrings(values, attribute, descending);
    }
  }

  return sortedItems;
};

// Get file contents.
function readFile(file) {
  return file.text();
}

// Remove site specific data from layout files, such as pushPins and panel filters.
function removeSiteSpecificData(site) {
  const rules = {
    // pageSchema
    components: { replaceWith: [], hasSibling: { key: 'filterType' } },
    // componentAttributesPanelSchema
    categoriesFilter: { replaceWith: [] },
    // componentsPanelSchema
    limitComponents: { replaceWith: [] },
    excludeComponents: { replaceWith: [] },
    // filesPanelSchema
    files: { replaceWith: [] },
    tags: { replaceWith: [] },
    // viewerPanelSchema
    pushPins: { replaceWith: [] },
    componentTooltips: { replaceWith: [] },
  };

  /**
   * Modifies a given property of an object according to a rule.
   * If the rule has an object called 'hasSibling', then it will modify the value only
   * if the given property has a sibling with the correct key (and optionally value, if specified).
   *    {
   *      Will modify all the keys named 'id' that has sibling property { type: 'pt' }
   *      id: { replaceWith: '', hasSibling: { key: 'type', value: 'pt' } },
   *
   *      Will modify all the keys named 'someOtherId' that has a sibling property 'type'
   *      someOtherId: { replaceWith: '', hasSibling: { key: 'type' } },
   *    }
   */
  function modify(key, context) {
    if (!Object.prototype.hasOwnProperty.call(rules, key)) {
      return;
    }

    const rule = rules[key];

    if (!Object.prototype.hasOwnProperty.call(rule, 'hasSibling')) {
      context[key] = rule.replaceWith;
      return;
    }

    if (
      Object.prototype.hasOwnProperty.call(rule.hasSibling, 'key') &&
      Object.prototype.hasOwnProperty.call(rule.hasSibling, 'value')
    ) {
      if (
        Object.prototype.hasOwnProperty.call(context, rule.hasSibling.key) &&
        context[rule.hasSibling.key] === rule.hasSibling.value
      ) {
        context[key] = rule.replaceWith;
      }

      return;
    }

    if (
      Object.prototype.hasOwnProperty.call(rule.hasSibling, 'key') &&
      Object.prototype.hasOwnProperty.call(context, rule.hasSibling.key)
    ) {
      context[key] = rule.replaceWith;
    }
  }

  /**
   * Recursive walk of a object, modifying each key/value pair that has a rule associated
   * with it.
   *
   * Context is the current scope of the key/value pair.
   * For instance:
   * {
   *   hello: { world: 123, bye: 345 }
   * }
   *
   * the keys 'world' and 'bye' share the same context, whereas 'hello' does not.
   */
  function walkAndModify(key, value, context) {
    if (Array.isArray(value) && Object.prototype.hasOwnProperty.call(rules, key)) {
      modify(key, context);
    } else if (value !== null && typeof value === 'object') {
      Object.entries(value).forEach(([k, v]) => {
        walkAndModify(k, v, value);
      });
    } else {
      modify(key, context);
    }
  }

  walkAndModify('', site, null);
}

/**
 * Similar to the num.toFixed() but it properly rounds numbers
 *
 * @param {String|Number} val Value to convert
 * @param {Number} num Number of decimals
 * @param {Boolean} fill Fill remaining decimals with zero
 */
function toNumberDecimals(value_, num = 3, fill = false) {
  let value = value_;

  if (typeof value === 'string') {
    value = Number.parseFloat(value);
  }

  if (Number.isNaN(value) || typeof value !== 'number') {
    throw new TypeError(`${value_} is not a number`);
  }

  const dec = 10 ** num;

  // Round it
  value = Math.round(value * dec) / dec;

  // convert to string
  value = value.toString();

  if (fill) {
    const baseNumber = value.split('.')[0];
    const fraction = value.split('.')[1] || '';

    value = [baseNumber, fraction.padEnd(num, '0')].join('.');
  }

  return value;
}

function capitalize(text = '') {
  return text[0].toUpperCase() + text.slice(1);
}

function toCapitalizedWords(string_) {
  const words = string_.match(/[A-Za-z][a-z]*/g) || [];

  return words.map((word) => capitalize(word)).join(' ');
}

function camelToSentence(text) {
  // assumes first char lowercase, e.g. 'camelCase' -> 'Camel case'
  const result = text.replaceAll(/([A-Z])/g, ' $1').toLowerCase();
  return capitalize(result);
}

// Ensures a URL has a protocol (http/https), otherwise prepends window.location.protocol
function withProtocol(url) {
  if (/^https?:\/\//i.test(url)) return url;
  return `${globalThis.location.protocol}//${url}`;
}

function downloadBlob(blob, filename = 'download.zip', type = '') {
  const url = globalThis.URL.createObjectURL(new Blob([blob], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'download';

  const clickHandler = () => {
    setTimeout(() => {
      globalThis.URL.revokeObjectURL(url);
      link.removeEventListener('click', clickHandler);
    }, 150);
  };

  link.addEventListener('click', clickHandler, false);
  link.click();
}

/**
 * Download a blob from a resource URL with optional Authorization header.
 * @param {blob} blob
 * @param {string} filename
 */
export async function downloadBlobFromUrl(blob, filename) {
  const url = globalThis.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download';
  document.body.append(a);
  a.click();
  a.remove();
  globalThis.URL.revokeObjectURL(url);
}

const getFilenameWithoutExtension = (filename) =>
  filename.slice(0, Math.max(0, filename.lastIndexOf('.')));
const getFileExtension = (filename) => filename.split('.').pop();

const formatVideoTime = (secondsFromStart) => {
  const hours = `${Math.floor(secondsFromStart / 60 / 60)}`.padStart(2, '0');
  const minutes = `${Math.floor(secondsFromStart / 60)}`.padStart(2, '0');
  const seconds = `${Math.floor(secondsFromStart % 60)}`.padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
};

const clientSizeContext = createContext({ change$: new Subject() });

function useClientSize() {
  const ref = useRef();
  const { change$ } = useContext(clientSizeContext);
  const [size, setSize] = useState({ height: 0, width: 0 });
  const resize = useCallback(() => {
    if (ref.current) {
      setSize((old) => {
        const { clientHeight: height, clientWidth: width } = ref.current;
        if (old.height === height && old.width === width) {
          return old;
        }
        return { height, width };
      });
    } else {
      setSize({ height: null, width: null });
    }
  }, [ref, setSize]);
  useEffect(() => {
    const sub = change$.subscribe(resize);
    return () => sub.unsubscribe();
  }, [change$, resize]);
  useEventListener('resize', resize);
  const refCallback = useCallback(
    (node) => {
      ref.current = node;
      if (node) {
        resize();
      }
    },
    [ref, resize]
  );
  return [size, refCallback];
}

const useClientSizeRefresh = () => {
  const { change$ } = useContext(clientSizeContext);
  return useCallback(() => setTimeout(() => change$.next(), 100), [change$]);
};

const useIndicatedCallback = (...callbackArguments) => {
  const callback = useCallback(...callbackArguments);
  const [isLoading, setIsLoading] = useState(false);
  const decoratedCallback = useCallback(
    async (...args) => {
      setIsLoading(true);
      try {
        const ressponse = await callback(...args);
        return ressponse;
      } finally {
        setIsLoading(false);
      }
    },
    [callback, setIsLoading]
  );
  return [decoratedCallback, isLoading];
};

const observableStateContext = createContext({ state$: null });
const ObservableStateProvider = ({ children }) => {
  const store = useStore();
  const subject = useMemo(() => new BehaviorSubject(store.getState()), [store]);
  useEffect(() => {
    const sub = store.subscribe(() => subject.next(store.getState()));
    return () => sub();
  }, [store, subject]);
  const context = useMemo(() => ({ state$: subject.asObservable() }), [subject]);
  return (
    <observableStateContext.Provider value={context}>{children}</observableStateContext.Provider>
  );
};

const useObservableState = () => {
  const { state$ } = useContext(observableStateContext);
  return state$;
};

const useSelect$ = (function_, dependencies = []) => {
  const state$ = useObservableState();
  return useMemo(() => state$.pipe(map(function_), distinctUntilChanged()), dependencies);
};

const useSubscribe = (observable, initialValue) => {
  const [state, setState] = useState(initialValue);
  useEffect(() => {
    if (!observable) {
      return () => 0;
    }
    setState(initialValue);
    const sub = observable.subscribe(setState);
    return () => {
      sub.unsubscribe();
    };
  }, [setState, observable]);
  return state;
};

const useSubscribedReference = (observable, initialValue) => {
  const ref = useRef(initialValue);
  useEffect(() => {
    const sub = observable.subscribe((value) => {
      ref.current = value;
    });
    return () => sub.unsubscribe();
  }, [observable]);
  return ref;
};

function exhaustMapWithTrailing(project) {
  return (source) =>
    defer(() => {
      const release = new Subject();

      return source.pipe(
        throttle(() => release, { leading: true, trailing: true }),
        exhaustMap((value, index) =>
          from(project(value, index)).pipe(finalize(() => release.next()))
        )
      );
    });
}

// Async hooks

function buildSharedContextInstance() {
  const store = {};

  function cache(factory, cacheKey) {
    if (!store[cacheKey]) {
      store[cacheKey] = new Observable((subscriber) => {
        const refresh$ = new BehaviorSubject();
        const continuation$ = new Subject();
        function refresh() {
          refresh$.next();
          return continuation$.pipe(take(1)).toPromise();
        }
        const sub = refresh$
          .pipe(
            exhaustMapWithTrailing(factory),
            tap(() => continuation$.next()),
            map((response) => ({ response, refresh }))
          )
          .subscribe(subscriber);
        return () => {
          sub.unsubscribe();
          delete store[cacheKey];
        };
      }).pipe(publishReplay(1), refCount());
    }
    return store[cacheKey];
  }
  cache.refresh = (...keys) =>
    Promise.all(
      keys
        .filter((key) => store[key])
        .map((key) =>
          store[key]
            .pipe(
              take(1),
              map((item) => item.refresh())
            )
            .toPromise()
        )
    );
  return { cache };
}

const sharedRequestContext = createContext(buildSharedContextInstance());

function useAsyncCache() {
  const { cache } = useContext(sharedRequestContext);
  return cache;
}

function getSharedCacheKey(prefix, ...args) {
  function cachify(arg) {
    if (Array.isArray(arg)) {
      return arg.map((item) => cachify(item)).join('|');
    }
    if (typeof arg === 'object') {
      throw new TypeError(
        'Objects are not supported for shared caching. Use primitives and arrays instead'
      );
    }
    return arg.toString();
  }
  return cachify([prefix.toLowerCase(), ...args]);
}

const cacheConnector = (cache, onUpdate) => {
  const keySet = new Set();
  const store = {};
  let updateSub = null;
  function unsub() {
    if (updateSub) {
      updateSub.unsubscribe();
      updateSub = null;
    }
  }
  return {
    begin() {
      unsub();
      keySet.clear();
    },
    cache(factory, cacheKey) {
      if (!store[cacheKey]) {
        const connectable = cache(factory, cacheKey).pipe(publishReplay(1));
        store[cacheKey] = { observable: connectable, sub: connectable.connect() };
      }
      keySet.add(cacheKey);
      return store[cacheKey].observable;
    },
    end() {
      Object.keys(store)
        .filter((key) => !keySet.has(key))
        .forEach((key) => {
          store[key].sub.unsubscribe();
          delete store[key];
        });
      keySet.clear();
      unsub();
      updateSub = merge(...Object.values(store).map((item) => item.observable.pipe(skip(1))))
        .pipe(debounceTime(100), take(1))
        .subscribe(onUpdate);
    },
    disconnect() {
      unsub();
      Object.keys(store).forEach((key) => {
        store[key].sub.unsubscribe();
        delete store[key];
      });
    },
  };
};

/**
 * Creates an async context used for running multiple async operations combined into one
 * observable result.
 * @param {(cache: <T>(factory: () => Promise<T>, cacheKey: string) => Promise<T>)} factory
 * @param {unknown[]} dependencies
 * @returns {{ response?: any, loading: boolean, error?: any, refresh: () => Promise<void>}}
 */
function useAsync$(factory, dependencies) {
  const factory$ = useMemo(() => new BehaviorSubject(factory), []);
  const updateTrigger$ = useMemo(() => new BehaviorSubject(), []);
  const cache = useAsyncCache();
  const connector = useMemo(
    () => cacheConnector(cache, () => updateTrigger$.next()),
    [cache, updateTrigger$]
  );
  useEffect(() => () => connector.disconnect(), [connector]);

  const refresh = useRef(undefined);
  const handleRefresh = useCallback(() => {
    if (isFunction(refresh.current)) {
      return refresh.current();
    }
    return Promise.resolve();
  }, []);

  useEffect(() => {
    if (factory$.value !== factory) {
      factory$.next(factory);
    }
  }, [factory$, ...dependencies]);

  const setLoading = useSetLoading();

  const observable = useMemo(
    () =>
      combineLatest([factory$, updateTrigger$]).pipe(
        tap(() => connector.begin()),
        tap(() => setLoading(true)),
        exhaustMapWithTrailing(async ([factoryFunction]) => {
          const refreshes = [];
          function wrapCache(function_, cacheKey, cacheDuration) {
            return connector
              .cache(function_, cacheKey, cacheDuration)
              .pipe(
                take(1),
                tap((item) => refreshes.push(item.refresh)),
                map(({ response: ressponse }) => ressponse)
              )
              .toPromise();
          }
          const response = await factoryFunction(wrapCache);
          refresh.current = () => Promise.all(refreshes.map((r) => r()));
          return { response, loading: false, refresh: handleRefresh };
        }),
        tap(() => connector.end()),
        catchError((error) => of({ error, loading: false, refresh: handleRefresh })),
        tap(() => setLoading(false))
      ),
    [factory$, connector, refresh, updateTrigger$]
  );
  return [observable, handleRefresh];
}

/**
 * Creates an async context used for running multiple async operations combined into one
 * observable result.
 * @param {(cache: <T>(factory: () => Promise<T>, cacheKey: string) => Promise<T>)} factory
 * @param {unknown[]} dependencies
 * @returns {{ response?: any, loading: boolean, error?: any, refresh: () => Promise<void>}}
 */
function useAsync(factory, dependencies) {
  const [observable, handleRefresh] = useAsync$(factory, dependencies);
  return useSubscribe(observable, { loading: true, refresh: handleRefresh });
}

function useSingleAsync(factory, ...cacheKeys) {
  if (cacheKeys.length === 0) {
    console.warn('Did you forget to add an appropriate cache key?');
  }
  const cacheKey = getSharedCacheKey(...cacheKeys);
  return useAsync((cache) => cache(() => factory(), cacheKey), cacheKey);
}

function useAsyncCallback(factory, dependencies) {
  const cache = useAsyncCache();
  return useCallback((...args) => factory(...args, cache), [cache, ...dependencies]);
}

// END Async hooks

const getUpdatedFields = (path, node, formData) => {
  const part = path.shift();
  if (Array.isArray(node[part])) {
    const index = path.shift();
    return {
      [part]: [
        ...node[part].slice(0, index),
        { ...node[part][index], ...getUpdatedFields(path, node[part][index], formData) },
        ...node[part].slice(index + 1),
      ],
    };
  }
  return {
    [part]: {
      ...node[part],
      ...(path.length > 0 ? getUpdatedFields(path, node[part], formData) : formData),
    },
  };
};

const getHexaDecimal = (hexColor) => Number.parseInt(hexColor.slice(1), 16);

const hexToRGB = (hex) => {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);

  return [r, g, b];
};
const removeTrailingSlash = (url) => url.replace(/\/$/, '');

const replacer = (match, params) => {
  const configValue = params[match.slice(1, -1)];
  if (configValue) {
    return configValue;
  }
  throw new Error(`URI replacement for ${match.slice(1, -1)} not found!`);
};

const downloadExcel = async (exportType, workbook, fileName, onExportCompleted) => {
  try {
    switch (exportType) {
      case 'xlsx': {
        const xlsxData = await workbook.xlsx.writeBuffer();
        downloadBlob(
          xlsxData,
          fileName,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        );
        break;
      }
      case 'csv': {
        const csvData = await workbook.csv.writeBuffer();
        downloadBlob(csvData, fileName, 'text/csv;charset=utf-8');
        break;
      }
      case 'xml': {
        downloadBlob(workbook, fileName, 'text/xml;charset=utf-8');
        break;
      }
      default: {
        const buffer = await workbook.xlsx.writeBuffer();
        downloadBlob(buffer, fileName, '');
      }
    }
    if (onExportCompleted) {
      onExportCompleted();
    }
  } catch (error) {
    console.error('<<<ERRROR>>>', error);
    console.error('Something Went Wrong', error.message);
  }
};

const formatDateString = (date) =>
  getLocalDateString(date, 'en-CA').replaceAll(/[^a-zA-Z0-9 ]/g, '');

const createWorkbook = () =>
  new ExcelJS.Workbook({
    created: new Date(),
    modified: new Date(),
    views: [
      {
        x: 0,
        y: 0,
        width: 10_000,
        height: 20_000,
        firstSheet: 0,
        activeTab: 1,
        visibility: 'visible',
      },
    ],
  });

const formatSeriesData = (seriesData, variables) =>
  seriesData.reduce((acc, series) => {
    const aggregate = series.aggregate ?? series.name?.split('.').pop();
    const itemDes = variables.find((v) => v.id === series.variable)?.itemDesignation;
    if (
      !acc.some(
        (index) =>
          index.name === series.variableName &&
          index.aggregate === aggregate &&
          index.itemDesignation === itemDes
      )
    ) {
      acc.push({
        name: series.variableName,
        id: `${series.variable}_${aggregate}`,
        aggregate,
        dateTimestamp: series.values.map((v) => new Date(v[0]).toISOString()),
        values: series.values.flatMap((v) => v[1]),
        itemDesignation: itemDes,
      });
    }
    return acc;
  }, []);

const sanitizeFileName = (value) => value.replaceAll(/[\s.<>:"/\\|?*]/g, '_');

function generateFileName(siteName, type, fromDate, toDate, suffix) {
  const formattedSiteName = sanitizeFileName(siteName);
  return `${formattedSiteName}-${type}-${fromDate}_${toDate}${suffix ? `-${suffix}` : ''}`;
}

const exportTimeSeriesToFile = async (data) => {
  const {
    seriesData,
    siteName,
    startDate,
    endDate,
    exportType,
    variables,
    granularity,
    onExportCompleted,
  } = data;
  const fromDate = formatDateString(startDate);
  const toDate = formatDateString(endDate);
  const granularityValue = GRANULARITY_OPTIONS.find((g) => g.value === granularity).label;
  const fileName = generateFileName(siteName, 'TimeSeries', fromDate, toDate, granularityValue);
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Time series');
  const formattedData = formatSeriesData(seriesData, variables);

  // header column
  const uniqueCols = [
    ...new Set(
      formattedData.map(({ name, id, aggregate, itemDesignation }) => ({
        header: `${name}${itemDesignation || ''} (${aggregate})`,
        key: id,
        width: 40,
        type: 'string',
      }))
    ),
  ];
  worksheet.columns = [
    { header: 'DateTime', key: 'timestamp', width: 30, type: 'string' },
    ...uniqueCols,
  ];

  worksheet.getRow(1).font = { bold: true };

  formattedData.forEach((ts) => {
    const timestampColumn = worksheet.getColumn('timestamp');
    if (!timestampColumn.values?.[2] && ts.dateTimestamp?.length > 0) {
      ts.dateTimestamp.forEach((timestamp, index) => {
        worksheet.getRow(index + 2).getCell('timestamp').value = timestamp;
      });
    }
    const colSignalValue = worksheet.getColumn(ts.id);
    if (colSignalValue) {
      colSignalValue.numFmt = '#,##0.0000';
      ts.values.forEach((v, index) => {
        worksheet.getRow(index + 2).getCell(ts.id).value = v ? Number.parseFloat(v) : '';
      });
    }
  });
  downloadExcel(exportType, workbook, fileName, onExportCompleted);
};

const createNestedXml = (attributes) => {
  let xml = '';
  for (const [key, value] of Object.entries(attributes)) {
    const tagName = key.charAt(0).toUpperCase() + key.slice(1);
    xml += `<${tagName}>${value}</${tagName}>`;
  }
  return xml;
};

// Omits the tag entirely instead of rendering "undefined" when no value is present.
const xmlTag = (tagName, value) =>
  value === undefined || value === null ? '' : `<${tagName}>${value}</${tagName}>`;

// Cloud sends epoch milliseconds (number); on-prem sends ISO strings. Accept both.
const toExportTimestamp = (timestamp) =>
  typeof timestamp === 'string' ? timestamp : new Date(timestamp).toISOString();

const exportEventsToFile = (data) => {
  const { siteName, startDate, endDate, exportType, eventType, events, onExportCompleted } = data;
  const fromDate = getLocalDateString(startDate, 'en-CA').replaceAll(/[^a-zA-Z0-9 ]/g, '');
  const toDate = getLocalDateString(endDate, 'en-CA').replaceAll(/[^a-zA-Z0-9 ]/g, '');
  const fileName = generateFileName(siteName, `${eventType}_events`, fromDate, toDate, null);

  if (exportType === 'xlsx') {
    const workbook = createWorkbook();
    const worksheet = workbook.addWorksheet('Events');

    switch (eventType) {
      case EVENT_TYPE_MACH: {
        // header column
        worksheet.columns = [
          { header: 'Timestamp', key: 'timestamp', width: 30, type: 'string' },
          { header: 'Severity', key: 'severity', width: 30, type: 'string' },
          { header: 'Point Group (Source)', key: 'point-group', width: 30, type: 'string' },
          { header: 'Message (Name)', key: 'message', width: 30, type: 'string' },
          { header: 'Label', key: 'false-alarm', width: 15, type: 'string' },
          { header: 'Active', key: 'active', width: 10, type: 'string' },
          { header: 'Sender', key: 'sender', width: 30, type: 'string' },
          { header: 'Point ID', key: 'point-id', width: 30, type: 'string' },
        ];
        worksheet.getRow(1).font = { bold: true };
        worksheet.getRow(1).alignment = { horizontal: 'center' };
        worksheet.getColumn(5).alignment = { horizontal: 'center' };

        const formattedIotEvents = events.map((event) => [
          toExportTimestamp(event.timestamp),
          event.context?.externalData?.severity || 'Unknown',
          event.remoteSource || event.pointGroup,
          event.message || event.text,
          event.isFalseAlarm || event.label?.toLowerCase() === 'false' ? 'False' : '',
          event.active ? '\u2714' : '',
          event.sender,
          event.origin || event.pointId,
        ]);

        worksheet.addRows(formattedIotEvents);
        break;
      }
      case EVENT_TYPE_DTMP: {
        // header column for DTMP events
        worksheet.columns = [
          { header: 'Timestamp', key: 'timestamp', width: 30, type: 'string' },
          { header: 'Severity', key: 'severity', width: 20, type: 'string' },
          { header: 'Message', key: 'message', width: 50, type: 'string' },
          { header: 'Message Type', key: 'message-type', width: 25, type: 'string' },
          { header: 'Algorithm Name', key: 'algorithm-name', width: 30, type: 'string' },
          { header: 'Algorithm Type', key: 'algorithm-type', width: 30, type: 'string' },
          { header: 'Identifier', key: 'additional-identifier', width: 35, type: 'string' },
          {
            header: 'Deviating Reference Designation',
            key: 'deviating-ref',
            width: 35,
            type: 'string',
          },
          { header: 'Recommendation', key: 'recommendation', width: 50, type: 'string' },
        ];
        worksheet.getRow(1).font = { bold: true };
        worksheet.getRow(1).alignment = { horizontal: 'center' };

        const formattedDtmpEvents = events.map((event) => [
          toExportTimestamp(event.timestamp),
          DTMP_SEVERITIES[event.severity] || 'Unknown',
          event.message,
          event.messageType || '',
          event.algorithmName || '',
          event.algorithmType || '',
          event.additionalIdentifier || '',
          event.deviatingReferenceDesignation || '',
          event.recommendation || '',
        ]);

        worksheet.addRows(formattedDtmpEvents);
        break;
      }
      case EVENT_TYPE_USER: {
        // handle user events

        // header column
        const userEventColumns = [
          { header: 'Start date', key: 'start', width: 25, type: 'string' },
          { header: 'End date', key: 'end', width: 25, type: 'string' },
          { header: 'Name', key: 'name', width: 30, type: 'string' },
          { header: 'Type', key: 'type', width: 20, type: 'string' },
          { header: 'State', key: 'state', width: 15, type: 'string' },
          { header: 'Activity Type', key: 'activity-type', width: 15, type: 'string' },
          { header: 'Created By', key: 'created-by', width: 15, type: 'string' },
          { header: 'Assigned to', key: 'assigned-to', width: 30, type: 'string' },
          { header: 'Execution start date', key: 'execution-start', width: 30, type: 'string' },
          { header: 'Execution end date', key: 'execution-end', width: 30, type: 'string' },
          { header: 'Description', key: 'description', width: 50, type: 'string' },
          { header: 'Tags', key: 'tags', width: 30, type: 'string' },
          { header: 'Components', key: 'components', width: 30, type: 'string' },
          { header: 'Attachments', key: 'attachments', width: 30, type: 'string' },
          { header: 'Has Related Events', key: 'related', width: 30, type: 'string' },
          { header: 'Attributes', key: 'attributes', width: 50, type: 'string' },
          { header: 'Part of series', key: 'series', width: 15, type: 'string' },
        ];
        const excludedColumnKeys = appConfig.onPremise
          ? new Set(['created-by', 'assigned-to'])
          : new Set();
        worksheet.columns = userEventColumns.filter(({ key }) => !excludedColumnKeys.has(key));
        worksheet.getRow(1).font = { bold: true };

        const formattedUserEvents = events.map((event) => ({
          start: `${new Date(event.from).toISOString().split('.')[0]}Z`,
          end: `${new Date(event.to).toISOString().split('.')[0]}Z`,
          name: event.name,
          type: capitalize(event.type),
          state: event.task ? event.state : '',
          'activity-type':
            event.task && event.planned
              ? 'planned'
              : event.task && !event.planned
                ? 'reactive'
                : '',
          'created-by': event.createdBy,
          'assigned-to': event.assignedTo,
          'execution-start': event.executionFrom ? `${event.executionFrom.split('.')[0]}Z` : null,
          'execution-end': event.executionTo ? `${event.executionTo.split('.')[0]}Z` : null,
          description: event.description,
          tags: event.tags.join(', '),
          components: event.components.join(', '),
          attachments: event.files.length > 0 ? '\u2714' : '',
          related: event.events.from && event.events.to ? '\u2714' : '',
          attributes: JSON.stringify(event.attributes),
          series: event.seriesId ? '\u2714' : '',
        }));

        worksheet.addRows(formattedUserEvents);
        break;
      }
      default: {
        break;
      }
    }

    downloadExcel(exportType, workbook, fileName, onExportCompleted);
  }

  if (exportType === 'xml') {
    let workbook = '<?xml version="1.0" encoding="utf-8"?>';
    workbook += '<ArchivedEvents>';

    switch (eventType) {
      case EVENT_TYPE_MACH: {
        events.forEach((event) => {
          workbook += '<Event>';
          workbook += `<EventDateTime>${toExportTimestamp(event.timestamp)}</EventDateTime>`;
          workbook += `<EventSeverity>${
            event.context?.externalData?.severity || 'Unknown'
          }</EventSeverity>`;
          workbook += `<PointGroup>${event.remoteSource || event.pointGroup}</PointGroup>`;
          workbook += `<EventText>${event.message || event.text}</EventText>`;
          if (event.isFalseAlarm || event.label?.toLowerCase() === 'false') {
            workbook += `<EventLabel>False</EventLabel>`;
          }
          workbook += `<EventActive>${event.active}</EventActive>`;
          workbook += `<Sender>${event.sender}</Sender>`;
          workbook += `<PointId>${event.origin || event.pointId}</PointId>`;
          workbook += '</Event>';
        });
        break;
      }
      case EVENT_TYPE_DTMP: {
        events.forEach((event) => {
          workbook += '<Event>';
          workbook += `<EventDateTime>${toExportTimestamp(event.timestamp)}</EventDateTime>`;
          workbook += `<EventSeverity>${DTMP_SEVERITIES[event.severity] || 'Unknown'}</EventSeverity>`;
          workbook += xmlTag('EventMessage', event.message);
          workbook += `<MessageType>${event.messageType || ''}</MessageType>`;
          workbook += `<AlgorithmName>${event.algorithmName || ''}</AlgorithmName>`;
          workbook += `<AlgorithmType>${event.algorithmType || ''}</AlgorithmType>`;
          workbook += `<AdditionalIdentifier>${event.additionalIdentifier || ''}</AdditionalIdentifier>`;
          workbook += `<DeviatingReferenceDesignation>${event.deviatingReferenceDesignation || ''}</DeviatingReferenceDesignation>`;
          workbook += `<Recommendation>${event.recommendation || ''}</Recommendation>`;
          workbook += '</Event>';
        });
        break;
      }
      case EVENT_TYPE_USER: {
        events.forEach((event) => {
          workbook += '<Event>';
          workbook += `<StartDate>${new Date(event.from).toISOString()}</StartDate>`;
          workbook += `<EndDate>${new Date(event.to).toISOString()}</EndDate>`;
          workbook += `<Name>${event.name}</Name>`;
          workbook += `<Type>${capitalize(event.type)}</Type>`;
          workbook += `<State>${event.task ? capitalize(event.state) : ''}</State>`;
          let activity = '';
          if (event.task) {
            activity = event.planned ? 'Planned' : 'Reactive';
          }
          workbook += `<ActivityType>${activity}</ActivityType>`;
          if (!appConfig.onPremise) {
            workbook += `<CreatedBy>${event.createdBy || ''}</CreatedBy>`;
            workbook += `<AssignedTo>${event.assignedTo || ''}</AssignedTo>`;
          }
          workbook += `<ExecutionStartDate>${event.executionFrom || ''}</ExecutionStartDate>`;
          workbook += `<ExecutionEndDate>${event.executionTo || ''}</ExecutionEndDate>`;
          workbook += `<Description>${event.description || ''}</Description>`;
          workbook += `<Tags>${event.tags ? event.tags.join(', ') : ''}</Tags>`;
          workbook += `<Components>${event.components ? event.components.join(', ') : ''}</Components>`;
          workbook += `<Attachments>${event.files.length > 0 ? 'True' : ''}</Attachments>`;
          workbook += `<HasRelatedEvents>${
            event.events.from && event.events.to ? 'True' : ''
          }</HasRelatedEvents>`;
          workbook += `<Attributes>${event.attributes ? createNestedXml(event.attributes) : ''}</Attributes>`;
          workbook += `<PartOfSeries>${event.seriesId ? 'True' : ''}</PartOfSeries>`;
          workbook += '</Event>';
        });
        break;
      }
      default: {
        break;
      }
    }
    workbook += '</ArchivedEvents>';
    downloadExcel(exportType, workbook, fileName, onExportCompleted);
  }
};

// Export analytics table data to Excel file (switchgear counter and motor runtime)
const exportAnalyticsToFile = (data) => {
  const { siteName, exportType, tableType, rows, dateRange, onExportCompleted } = data;

  const fromDate = dateRange?.from
    ? new Date(dateRange.from).toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0];
  const toDate = dateRange?.to
    ? new Date(dateRange.to).toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0];

  const fileName = generateFileName(siteName, `analytics_${tableType}`, fromDate, toDate, null);

  const columnsByTableType = {
    'switchgear-counter': [
      { header: 'Status', key: 'status', width: 15 },
      { header: 'Point Group', key: 'point_group', width: 25 },
      { header: 'Phase', key: 'phase', width: 15 },
      { header: 'Operation Count', key: 'operation_count', width: 20 },
      { header: 'Last Operation Time', key: 'last_operation_time', width: 25 },
      { header: 'Days Since Last Operation', key: 'days_since_last_operation', width: 28 },
    ],
    'motor-runtime': [
      { header: 'Status', key: 'status', width: 15 },
      { header: 'Point Group', key: 'point_group', width: 25 },
      { header: 'Number of Starts', key: 'number_of_starts', width: 20 },
      { header: 'Starts per Day', key: 'starts_per_day', width: 18 },
      { header: 'Run Time (hours)', key: 'run_time_hours', width: 20 },
      { header: 'Run Time (%)', key: 'run_time_percent', width: 16 },
    ],
  };

  const columns = columnsByTableType[tableType] || columnsByTableType['switchgear-counter'];

  const excelStatusLabels = {
    'switchgear-counter': { ok: 'Open', unknown: 'Closed' },
    'motor-runtime': { ok: 'Running', unknown: 'Stopped' },
  };
  const statusLabels = excelStatusLabels[tableType] || excelStatusLabels['switchgear-counter'];

  if (exportType === 'xlsx') {
    const workbook = createWorkbook();
    const worksheet = workbook.addWorksheet('Analytics');

    worksheet.columns = columns;
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).alignment = { horizontal: 'center' };

    const phaseLabels = { 1: 'L1', 2: 'L2', 3: 'L3' };
    const formattedRows = rows.map((row) =>
      columns.map((col) => {
        const value = row[col.key] ?? '';
        if (col.key === 'status') return statusLabels[value] || value;
        if (col.key === 'phase') return phaseLabels[value] ?? '\u2013';
        return value;
      })
    );

    worksheet.addRows(formattedRows);

    downloadExcel(exportType, workbook, fileName, onExportCompleted);
  }
};

// Export alarms to Excel file
const exportAlarmsToFile = (data) => {
  const {
    dateRange,
    siteName,
    exportType,
    alarmState,
    alarms,
    usersById = {},
    onExportCompleted,
  } = data;
  const fileName = generateFileName(
    siteName,
    `alarms_${alarmState}`,
    new Date(dateRange?.startDate || Date.now()).toISOString().split('T')[0],
    new Date(dateRange?.endDate || Date.now()).toISOString().split('T')[0],
    null
  );

  if (exportType === 'xlsx') {
    const workbook = createWorkbook();
    const worksheet = workbook.addWorksheet('Alarms');

    // header column - build then filter based on appConfig.onPremise
    let columns = [
      { header: 'Alarm Name', key: 'alarm-name', width: 30, type: 'string' },
      { header: 'Equipment', key: 'equipment', width: 30, type: 'string' },
      { header: 'Highest Severity', key: 'highest-severity', width: 20, type: 'string' },
      { header: 'Latest Severity', key: 'latest-severity', width: 20, type: 'string' },
      { header: 'First Occurrence', key: 'first-occurrence', width: 25, type: 'string' },
      { header: 'Last Occurrence', key: 'last-occurrence', width: 25, type: 'string' },
      { header: 'State', key: 'state', width: 15, type: 'string' },
      { header: 'Acknowledged By', key: 'acknowledged-by', width: 20, type: 'string' },
      { header: 'Acknowledged At', key: 'acknowledged-at', width: 25, type: 'string' },
      { header: 'Resolved By', key: 'resolved-by', width: 20, type: 'string' },
      { header: 'Resolved At', key: 'resolved-at', width: 25, type: 'string' },
    ];

    // remove acknowledged/resolved columns based on alarm state
    const suppressedKeysByState = {
      new: ['acknowledged-by', 'acknowledged-at', 'resolved-by', 'resolved-at'],
      acknowledged: ['resolved-by', 'resolved-at'],
    };
    const suppressedKeys = new Set(suppressedKeysByState[alarmState] || []);

    if (appConfig.onPremise) {
      // Remove acknowledged/resolved columns on on-prem installs
      suppressedKeys.add('acknowledged-by');
      suppressedKeys.add('acknowledged-at');
      suppressedKeys.add('resolved-by');
      suppressedKeys.add('resolved-at');
    }

    columns = columns.filter((c) => !suppressedKeys.has(c.key));

    worksheet.columns = columns;

    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).alignment = { horizontal: 'center' };

    // Build rows as objects keyed by column.key to keep ordering in sync with filtered columns
    const formattedAlarms = alarms.map((alarm) => {
      const row = {};
      columns.forEach((col) => {
        switch (col.key) {
          case 'alarm-name': {
            row[col.key] = alarm.algorithmName || '';
            break;
          }
          case 'equipment': {
            row[col.key] = alarm.deviatingReferenceDesignation || '';
            break;
          }
          case 'highest-severity': {
            row[col.key] = alarm.highestSeverity || '';
            break;
          }
          case 'latest-severity': {
            row[col.key] = alarm.latestSeverity || '';
            break;
          }
          case 'first-occurrence': {
            row[col.key] = alarm.firstOccurrence || '';
            break;
          }
          case 'last-occurrence': {
            row[col.key] = alarm.lastOccurrence || '';
            break;
          }
          case 'state': {
            row[col.key] = alarm.state || '';
            break;
          }
          case 'acknowledged-by': {
            row[col.key] =
              (alarm.acknowledgedBy && usersById[alarm.acknowledgedBy]?.name) ||
              alarm.acknowledgedBy ||
              '';
            break;
          }
          case 'acknowledged-at': {
            row[col.key] = alarm.acknowledgedAt || '';
            break;
          }
          case 'resolved-by': {
            row[col.key] =
              (alarm.resolvedBy && usersById[alarm.resolvedBy]?.name) || alarm.resolvedBy || '';
            break;
          }
          case 'resolved-at': {
            row[col.key] = alarm.resolvedAt || '';
            break;
          }
          default: {
            row[col.key] = '';
          }
        }
      });
      return row;
    });

    worksheet.addRows(formattedAlarms);

    downloadExcel(exportType, workbook, fileName, onExportCompleted);
  }
};

// removes key, value pairs where value is empty string or undefined.
const cleanFormData = (data) => {
  if (Array.isArray(data)) {
    return data.map((item) => cleanFormData(item));
  }

  if (typeof data === 'object' && data !== null) {
    return Object.entries(data).reduce((acc, [key, value]) => {
      const cleanedValue = typeof value === 'string' ? value.trim() : value;
      if (cleanedValue !== '' && cleanedValue !== undefined && cleanedValue !== null) {
        if (typeof cleanedValue === 'object') {
          return { ...acc, [key]: cleanFormData(cleanedValue) };
        }
        return { ...acc, [key]: cleanedValue };
      }
      return acc;
    }, {});
  }

  return data;
};

// coerces attribute lastValue to defined dataType
const withTypedValues = (attribute) => {
  switch (attribute.dataType) {
    case 'number': {
      return { ...attribute, lastValue: Number.parseFloat(attribute.lastValue) };
    }
    // rjsf boolean select expects string
    // case 'boolean':
    // case 'string':
    default: {
      return attribute;
    }
  }
};

// extracts catergoies for component attributes
const extractCategories = (comps) => [
  ...comps.reduce((acc, comp) => {
    (comp.asset.variables || []).forEach((attribute) => {
      if (attribute?.category) {
        acc.add(attribute.category);
      }
    });
    return acc;
  }, new Set()),
];

const mailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const validateEmail = (value) => !!mailRegex.test(value);

const phoneRegex = /^\+([ -]?\d){6,14}\d$/;

const isElementOverFlowing = (element) => element.offsetWidth < element.scrollWidth;

const useVariableSubscription = (panelId, variableIds = []) => {
  const dispatch = useDispatch();
  // Keyed by content: a new array with the same ids must not remove and re-add the subscription.
  const variableKey = variableIds.join(',');
  useEffect(() => {
    if (!variableKey || !panelId) return;
    const subscribedIds = variableKey.split(',');
    dispatch(addVariableSubscriptions(panelId, subscribedIds));

    return () => {
      dispatch(removeVariableSubscriptions(panelId, subscribedIds));
    };
  }, [variableKey, panelId, dispatch]);
};

/**
 * Download a blob zip.
 * @param {blob} blob
 * @param {string} filename
 * @returns {Promise<string[]>}
 */
export async function processAndDownloadZip(blob, filename) {
  let failedFiles = [];
  let finalBlob = blob;

  try {
    // Handle failed files in zip folder
    const zip = await JSZip.loadAsync(blob);
    const failedFile = zip.file('failed.json');
    if (failedFile) {
      const failedJson = await failedFile.async('string');
      const parsed = JSON.parse(failedJson);
      failedFiles = parsed.failedFiles || [];
      zip.remove('failed.json');
      finalBlob = await zip.generateAsync({ type: 'blob' });
    }
  } catch (error) {
    console.warn('Zip parsing failed:', error);
  }

  const url = globalThis.URL.createObjectURL(finalBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'download.zip';
  document.body.append(a);
  a.click();
  a.remove();
  globalThis.URL.revokeObjectURL(url);

  return failedFiles;
}

const useMemoizedDeepComparison = (value) => {
  const ref = useRef();

  if (JSON.stringify(ref.current) !== JSON.stringify(value)) {
    ref.current = value;
  }

  return ref.current;
};

const toKebabTitleCase = (string_) =>
  string_.replaceAll(/([a-z])([A-Z])/g, '$1-$2').replace(/^./, (c) => c.toUpperCase());

export {
  upsert,
  formatBytes,
  getUniqueId,
  getRealm,
  checkNetworkStatus,
  useComponentId,
  useEventListener,
  useParentOnlyClickHandler,
  useBackgroundFreeze,
  useDoubleClick,
  useScrollBlock,
  useActiveSiteId,
  useInterval,
  useForceUpdate,
  useClientSize,
  useClientSizeRefresh,
  debounce,
  useDebounce,
  useThrottledCallback,
  sortNumbers,
  sortStrings,
  sortDates,
  sort,
  removeSiteSpecificData,
  toNumberDecimals as toNumDecimals,
  capitalize,
  toCapitalizedWords,
  camelToSentence,
  getFilenameWithoutExtension as getFilenameWithoutExt,
  getFileExtension,
  downloadBlob,
  formatVideoTime,
  truncateFilename,
  updateSettingsParameters as updateSettingsParams,
  useIndicatedCallback,
  exhaustMapWithTrailing,
  useSubscribe,
  useSubscribedReference as useSubscribedRef,
  useSelect$,
  useObservableState,
  ObservableStateProvider,
  getSharedCacheKey,
  useAsync$,
  useAsync,
  useSingleAsync,
  useAsyncCallback,
  readFile,
  getHexaDecimal,
  isFunction,
  removeTrailingSlash,
  replacer,
  hexToRGB,
  getUpdatedFields,
  exportTimeSeriesToFile,
  exportEventsToFile,
  exportAlarmsToFile,
  exportAnalyticsToFile,
  formatDateString,
  cleanFormData,
  withTypedValues,
  extractCategories,
  validateEmail,
  mailRegex,
  phoneRegex,
  isElementOverFlowing,
  withProtocol,
  useVariableSubscription,
  useMemoizedDeepComparison,
  createWorkbook,
  downloadExcel,
  toKebabTitleCase,
  sanitizeFileName,
};
export { getNested } from './datetimeUtils';
